"use strict";
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.finleyBufferToCsvArray = exports.defaultParseConfig = exports.hiddenCharacterRegex = void 0;
const Papa = __importStar(require("papaparse"));
const finleyBuffer_1 = require("../finleyBuffer");
exports.hiddenCharacterRegex = /[\u200B-\u200D\uFEFF]/g;
const stripCarriageReturn = (str) => str.charAt(str.length - 1) === '\r' ? str.slice(0, str.length - 1) : str;
const defaultNewLine = '\n';
// Configuration options that are not meant to be overridden by external callers
exports.defaultParseConfig = {
    // Papaparse's automatic line-ending detection does not robustly handle files that use line
    // endings inconsistently. We force it to treat \n as the newline character. This may leave an
    // extra \r in the last column of every line, which we then need to strip out.
    newline: defaultNewLine,
    header: false,
    transform: stripCarriageReturn,
    transformHeader: stripCarriageReturn,
    quoteChar: '"',
    escapeChar: '"',
    skipEmptyLines: true,
};
// Internal default
const MB = Math.pow(1024, 2);
const defaultChunkSize = 50 * MB;
/**
 * Assumptions:
 * 1) Papa parse does not handle header parsing consistently. Always handle it ourselves.
 * 2) Header option will always be set to true in our system even though it is false by default for Papa Parse. Finley usage is expected to be a formatted CSV object (string[] output can be implemented separately), but not necessarily with expectation of being sanitized.
 * 3) Dynamic typing is not required by default and is handled by our sanitizer logic rather than Papa parse configuration
 * 4) Specific character sets can be removed if specified through the regex option. This may need to be a general default but isolated for use in only some cases at the moment.
 */
const finleyBufferToCsvArray = ({ buffer, delimiter = ',', dynamicTyping = false, characterRemovalRegex, headerRenameFunction, chunkSize = defaultChunkSize, // For internal use and testing purposes
 }) => {
    // ----- BUFFER HANDLING -----
    const chunks = (0, finleyBuffer_1.splitFinleyBuffer)(buffer, chunkSize);
    // ----- CSV PARSING -----
    const output = {
        originalHeaders: [],
        csvArray: [],
        errors: [], // Currently unused
    };
    // Override default parser configurations
    const parseConfig = Object.assign(Object.assign({}, exports.defaultParseConfig), { delimiter,
        dynamicTyping });
    const decoder = new TextDecoder('utf-8');
    let newlineIndex = -1;
    let currentChunk = chunks.shift();
    let headerRowChunk = new Uint8Array();
    // This initial block of code through L142 where the originalHeaders are updated purely handles header parsing (all body parsing occurs afterwards)
    // Given chunks 1, 2, and 3 where parts of the header exist in 1 and 2:
    // Shift() the first chunk and iterate across the full chunk until a newline is reached
    // If the current chunk does not have the new line, append it to headerRowChunk (running buffer to hold the full set of chunks until header is complete)
    // As we iterate across chunks, when the new line is finally reached, stop there and then take the remainder of the current chunk and set it to the first position of the chunks array (body starts after newline following header row)
    // The loop wil collect and combine all chunks relevant to the header row until it is completely full
    // eslint-disable-next-line no-restricted-syntax
    while (currentChunk && newlineIndex < 0) {
        // eslint-disable-next-line no-restricted-syntax
        for (let idx = 0; idx < currentChunk.length; idx += 1) {
            if (String.fromCharCode(currentChunk[idx]) === defaultNewLine) {
                // Set newline index to terminate loop
                newlineIndex = idx;
                // Append matching portion to header row chunk (including newline)
                // Include newline in header string so that papa parse can parse the full header line
                headerRowChunk = (0, finleyBuffer_1.combineBuffers)([
                    headerRowChunk,
                    currentChunk.subarray(0, newlineIndex + 1),
                ]);
                // Set first chunk to start after newline character
                chunks.unshift(currentChunk.subarray(newlineIndex + 1, currentChunk.length));
                // Break to exit from for loop
                break;
            }
        }
        // If newlineIndex was updated, stop evaluation
        if (newlineIndex >= 0)
            break;
        // If reached here, that means chunk is finished, combine with header row chunk and go to next chunk
        headerRowChunk = (0, finleyBuffer_1.combineBuffers)([headerRowChunk, currentChunk]);
        currentChunk = chunks.shift();
    }
    // Convert header row buffer to string
    let headerStringChunk = decoder.decode(headerRowChunk);
    // Remove characters if regex was provided
    if (characterRemovalRegex) {
        headerStringChunk = headerStringChunk.replace(characterRemovalRegex, '');
    }
    const { data: headerData } = Papa.parse(headerStringChunk, parseConfig);
    const headerRow = [];
    // If header rename function was provided, modify headers before proceeding
    for (const col of headerData[0]) {
        if (headerRenameFunction) {
            headerRow.push(headerRenameFunction(col));
        }
        else {
            headerRow.push(col);
        }
    }
    output.originalHeaders = headerRow;
    // ----- CSV BODY HANDLING -----
    let remainingRow = '';
    for (const [chunkIndex, chunk] of chunks.entries()) {
        // Convert buffer chunk to string
        let stringChunk = decoder.decode(chunk);
        // Strip characters if specified
        if (characterRemovalRegex) {
            stringChunk = stringChunk.replace(characterRemovalRegex, '');
        }
        // Prepend remaining row and continue with next string chunk
        const inputCsv = `${remainingRow}${stringChunk}`;
        const { data, errors } = Papa.parse(inputCsv, parseConfig);
        // For all chunks except the final one, remove last element of data because it may be incomplete
        // If the last row is incomplete for the final chunk, it is a data quality issue and not a CSV processing problem
        if (chunkIndex < chunks.length - 1) {
            // Remove last element, if any
            data.pop();
            // Handle remaining row portion depending on number of valid rows
            if (data.length) {
                // At least one full row was added (combined with the prior remaining row, if any)
                // There will be at least one newline separator present in the current string chunk
                // Reset the remaining row to match the part of the string moving forward from the last newline
                const lastNewlineIndex = stringChunk.lastIndexOf(defaultNewLine);
                const remainingStringChunk = stringChunk.slice(lastNewlineIndex + 1, stringChunk.length);
                remainingRow = remainingStringChunk;
            }
            else {
                // No full rows were added
                // It is possible that no portion of the prior remaining row was valid
                // Append the string chunk from this round to the prior remaining row
                remainingRow += stringChunk;
            }
        }
        // Add fully formed rows to output
        data.forEach((row) => {
            const convertedRow = {};
            headerRow.forEach((header, columnIdx) => {
                convertedRow[header] = row[columnIdx];
            });
            output.csvArray.push(convertedRow);
        });
        // Add errors if any
        errors.forEach((error) => output.errors.push(error));
    }
    return output;
};
exports.finleyBufferToCsvArray = finleyBufferToCsvArray;
//# sourceMappingURL=buffer.js.map